import { useState, useEffect } from 'react';
import { Key, Save, AlertTriangle, CheckCircle, Store, FlaskConical, Loader2, Cpu, RefreshCw, Radio, Zap, Trash2, Plus, X } from 'lucide-react';
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Button, Input } from '../components/ui';
import { testApiKey, listAvailableModels, AvailableModel, registerShopifyWebhooks } from '../services/api';
import './AdminSettingsPage.css';


interface APISettings {
    geminiApiKey?: string;
    openaiApiKey?: string;
}

type TestStatus = 'idle' | 'testing' | 'success' | 'error';

interface ShopInfo {
    id: string;
    name: string;
    domain: string;
    webhooksRegistered?: string[];
    webhooksRegisteredAt?: any;
}

export function AdminSettingsPage() {
    const [geminiKey, setGeminiKey] = useState('');

    // Webhook management state
    const [shops, setShops] = useState<ShopInfo[]>([]);
    const [webhookLoadingShop, setWebhookLoadingShop] = useState<string | null>(null);
    const [webhookMessage, setWebhookMessage] = useState<{ shopId: string; type: 'success' | 'error'; text: string } | null>(null);
    const [openaiKey, setOpenaiKey] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [hasExistingKeys, setHasExistingKeys] = useState<Record<string, boolean>>({});

    // Track which fields have been modified (dirty tracking)
    const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});

    // Test states
    const [geminiTestStatus, setGeminiTestStatus] = useState<TestStatus>('idle');
    const [geminiTestMessage, setGeminiTestMessage] = useState('');
    const [openaiTestStatus, setOpenaiTestStatus] = useState<TestStatus>('idle');
    const [openaiTestMessage, setOpenaiTestMessage] = useState('');

    // Shop connection state
    const [showAddShop, setShowAddShop] = useState(false);
    const [newShopDomain, setNewShopDomain] = useState('');
    const [newShopAccessToken, setNewShopAccessToken] = useState('');
    const [newShopClientId, setNewShopClientId] = useState('');
    const [newShopClientSecret, setNewShopClientSecret] = useState('');
    const [shopAuthMode, setShopAuthMode] = useState<'token' | 'credentials'>('credentials');
    const [isConnectingShop, setIsConnectingShop] = useState(false);
    const [shopConnectionError, setShopConnectionError] = useState('');
    const [shopConnectionSuccess, setShopConnectionSuccess] = useState('');

    // Selected models from API (stored in Firestore)
    const [selectedGeminiModels, setSelectedGeminiModels] = useState<Set<string>>(new Set());
    const [selectedOpenaiModels, setSelectedOpenaiModels] = useState<Set<string>>(new Set());

    // Dynamically fetched available models from API
    const [geminiApiModels, setGeminiApiModels] = useState<AvailableModel[]>([]);
    const [openaiApiModels, setOpenaiApiModels] = useState<AvailableModel[]>([]);
    const [isLoadingGeminiModels, setIsLoadingGeminiModels] = useState(false);
    const [isLoadingOpenaiModels, setIsLoadingOpenaiModels] = useState(false);
    const [geminiModelsError, setGeminiModelsError] = useState<string | null>(null);
    const [openaiModelsError, setOpenaiModelsError] = useState<string | null>(null);

    // Function to fetch models from Gemini API
    const fetchGeminiModels = async () => {
        setIsLoadingGeminiModels(true);
        setGeminiModelsError(null);
        try {
            const result = await listAvailableModels('gemini');
            setGeminiApiModels(result.models);
        } catch (err: any) {
            console.error('Error fetching Gemini models:', err);
            setGeminiModelsError(err.message || 'Erreur lors de la récupération des modèles');
        } finally {
            setIsLoadingGeminiModels(false);
        }
    };

    // Function to fetch models from OpenAI API
    const fetchOpenaiModels = async () => {
        setIsLoadingOpenaiModels(true);
        setOpenaiModelsError(null);
        try {
            const result = await listAvailableModels('openai');
            setOpenaiApiModels(result.models);
        } catch (err: any) {
            console.error('Error fetching OpenAI models:', err);
            setOpenaiModelsError(err.message || 'Erreur lors de la récupération des modèles');
        } finally {
            setIsLoadingOpenaiModels(false);
        }
    };

    // Load existing settings on mount
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settingsDoc = await getDoc(doc(db, 'settings', 'api_keys'));
                if (settingsDoc.exists()) {
                    const data = settingsDoc.data() as APISettings;
                    setHasExistingKeys({
                        gemini: !!data.geminiApiKey,
                        openai: !!data.openaiApiKey,
                    });
                }

                // Load selected models from Firestore
                const selectedModelsDoc = await getDoc(doc(db, 'settings', 'selected_models'));
                if (selectedModelsDoc.exists()) {
                    const data = selectedModelsDoc.data();
                    if (data.gemini && Array.isArray(data.gemini)) {
                        setSelectedGeminiModels(new Set(data.gemini));
                    }
                    if (data.openai && Array.isArray(data.openai)) {
                        setSelectedOpenaiModels(new Set(data.openai));
                    }
                }
            } catch (error) {
                console.error('Error loading settings:', error);
            }
        };

        loadSettings();
    }, []);

    // Load connected shops
    useEffect(() => {
        const loadShops = async () => {
            try {
                const shopsSnapshot = await getDocs(collection(db, 'shops'));
                const shopsData: ShopInfo[] = [];
                shopsSnapshot.forEach((doc) => {
                    const data = doc.data();
                    shopsData.push({
                        id: doc.id,
                        name: data.name,
                        domain: data.domain,
                        webhooksRegistered: data.webhooksRegistered,
                        webhooksRegisteredAt: data.webhooksRegisteredAt,
                    });
                });
                setShops(shopsData);
            } catch (error) {
                console.error('Error loading shops:', error);
            }
        };
        loadShops();
    }, []);

    // Handle webhook registration
    const handleRegisterWebhooks = async (shopId: string) => {
        setWebhookLoadingShop(shopId);
        setWebhookMessage(null);

        try {
            const result = await registerShopifyWebhooks(shopId);
            if (result.success && result.registered.length > 0) {
                setWebhookMessage({
                    shopId,
                    type: 'success',
                    text: `Webhooks enregistrés : ${result.registered.join(', ')}`
                });
                // Update local state
                setShops(prev => prev.map(shop =>
                    shop.id === shopId
                        ? { ...shop, webhooksRegistered: result.registered, webhooksRegisteredAt: new Date() }
                        : shop
                ));
            } else {
                setWebhookMessage({
                    shopId,
                    type: 'error',
                    text: 'Aucun webhook enregistré. Vérifiez les permissions de l\'app Shopify.'
                });
            }
        } catch (error: any) {
            setWebhookMessage({
                shopId,
                type: 'error',
                text: error.message || 'Erreur lors de l\'enregistrement des webhooks'
            });
        } finally {
            setWebhookLoadingShop(null);
        }
    };

    // Toggle model selection for Gemini
    const toggleGeminiModelSelection = (modelId: string) => {
        setSelectedGeminiModels(prev => {
            const newSet = new Set(prev);
            if (newSet.has(modelId)) {
                newSet.delete(modelId);
            } else {
                newSet.add(modelId);
            }
            return newSet;
        });
    };

    // Toggle model selection for OpenAI
    const toggleOpenaiModelSelection = (modelId: string) => {
        setSelectedOpenaiModels(prev => {
            const newSet = new Set(prev);
            if (newSet.has(modelId)) {
                newSet.delete(modelId);
            } else {
                newSet.add(modelId);
            }
            return newSet;
        });
    };



    const handleTestGemini = async () => {
        if (!geminiKey.trim()) {
            setGeminiTestStatus('error');
            setGeminiTestMessage('Entrez une clé API à tester');
            return;
        }

        setGeminiTestStatus('testing');
        setGeminiTestMessage('');

        try {
            const result = await testApiKey({ provider: 'gemini', apiKey: geminiKey.trim() });
            if (result.success) {
                setGeminiTestStatus('success');
                setGeminiTestMessage(result.message);
            } else {
                setGeminiTestStatus('error');
                setGeminiTestMessage(result.message);
            }
        } catch (error: any) {
            setGeminiTestStatus('error');
            setGeminiTestMessage(error.message || 'Erreur de test');
        }
    };

    const handleTestOpenai = async () => {
        if (!openaiKey.trim()) {
            setOpenaiTestStatus('error');
            setOpenaiTestMessage('Entrez une clé API à tester');
            return;
        }

        setOpenaiTestStatus('testing');
        setOpenaiTestMessage('');

        try {
            const result = await testApiKey({ provider: 'openai', apiKey: openaiKey.trim() });
            if (result.success) {
                setOpenaiTestStatus('success');
                setOpenaiTestMessage(result.message);
            } else {
                setOpenaiTestStatus('error');
                setOpenaiTestMessage(result.message);
            }
        } catch (error: any) {
            setOpenaiTestStatus('error');
            setOpenaiTestMessage(error.message || 'Erreur de test');
        }
    };

    // Connect a new Shopify store
    const handleConnectShop = async () => {
        let domain = newShopDomain.trim().toLowerCase();
        if (!domain.includes('.')) {
            domain = `${domain}.myshopify.com`;
        }
        domain = domain.replace(/^https?:\/\//, '');
        domain = domain.replace(/\/$/, '');

        if (!domain || domain.length < 5) {
            setShopConnectionError('Entrez un domaine Shopify valide');
            return;
        }
        if (shopAuthMode === 'token' && !newShopAccessToken.trim()) {
            setShopConnectionError('Admin API access token requis');
            return;
        }
        if (shopAuthMode === 'credentials' && (!newShopClientId.trim() || !newShopClientSecret.trim())) {
            setShopConnectionError('Client ID et Client Secret requis');
            return;
        }

        setIsConnectingShop(true);
        setShopConnectionError('');
        setShopConnectionSuccess('');

        try {
            const functions = getFunctions();
            const connectShopifyStore = httpsCallable(functions, 'connectShopifyStore');
            const payload: any = { domain };
            if (shopAuthMode === 'token') {
                payload.accessToken = newShopAccessToken.trim();
            } else {
                payload.clientId = newShopClientId.trim();
                payload.clientSecret = newShopClientSecret.trim();
            }
            const result = await connectShopifyStore(payload);

            const data = result.data as any;
            if (data.success) {
                setShopConnectionSuccess(`Boutique "${data.shopName}" connectée avec succès !`);
                setNewShopDomain('');
                setNewShopAccessToken('');
                setNewShopClientId('');
                setNewShopClientSecret('');
                setShowAddShop(false);
                // Reload shops
                const shopsSnapshot = await getDocs(collection(db, 'shops'));
                const shopsData: ShopInfo[] = [];
                shopsSnapshot.forEach((doc) => {
                    const d = doc.data();
                    shopsData.push({
                        id: doc.id,
                        name: d.name,
                        domain: d.domain,
                        webhooksRegistered: d.webhooksRegistered,
                        webhooksRegisteredAt: d.webhooksRegisteredAt,
                    });
                });
                setShops(shopsData);
                setTimeout(() => setShopConnectionSuccess(''), 5000);
            }
        } catch (error: any) {
            console.error('Connect shop error:', error);
            const msg = error?.details || error?.message || 'Erreur lors de la connexion';
            setShopConnectionError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setIsConnectingShop(false);
        }
    };

    // Delete a shop
    const handleDeleteShop = async (shopId: string, shopName: string) => {
        if (!confirm(`Êtes-vous sûr de vouloir déconnecter "${shopName}" ?`)) return;
        try {
            await deleteDoc(doc(db, 'shops', shopId));
            setShops(prev => prev.filter(s => s.id !== shopId));
        } catch (error) {
            console.error('Error deleting shop:', error);
            alert('Erreur lors de la suppression');
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveSuccess(false);

        try {
            const apiSettings: Record<string, string> = {};

            // Only include fields that have been explicitly modified (dirty)
            if (dirtyFields.geminiApiKey && geminiKey.trim()) {
                apiSettings.geminiApiKey = geminiKey.trim();
                console.log('[Settings] Saving Gemini API key (modified)');
            }
            if (dirtyFields.openaiApiKey && openaiKey.trim()) {
                apiSettings.openaiApiKey = openaiKey.trim();
                console.log('[Settings] Saving OpenAI API key (modified)');
            }

            // Save API keys only if at least one field was modified
            if (Object.keys(apiSettings).length > 0) {
                console.log('[Settings] Saving modified API keys:', Object.keys(apiSettings));
                await setDoc(doc(db, 'settings', 'api_keys'), apiSettings, { merge: true });
            } else {
                console.log('[Settings] No API key fields were modified, skipping API keys save');
            }

            // Save selected models with their info from API
            const selectedModelsData: Record<string, any> = {
                gemini: Array.from(selectedGeminiModels),
                openai: Array.from(selectedOpenaiModels),
                geminiModelsInfo: geminiApiModels
                    .filter(m => selectedGeminiModels.has(m.id))
                    .map(m => ({ id: m.id, displayName: m.displayName, type: m.type })),
                openaiModelsInfo: openaiApiModels
                    .filter(m => selectedOpenaiModels.has(m.id))
                    .map(m => ({ id: m.id, displayName: m.displayName, type: m.type })),
            };
            await setDoc(doc(db, 'settings', 'selected_models'), selectedModelsData);

            setHasExistingKeys(prev => ({
                ...prev,
                gemini: prev.gemini || !!geminiKey.trim(),
                openai: prev.openai || !!openaiKey.trim(),
            }));

            setGeminiKey('');
            setOpenaiKey('');
            setDirtyFields({}); // Reset dirty tracking after save
            setGeminiTestStatus('idle');
            setOpenaiTestStatus('idle');

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error: any) {
            console.error('Error saving settings:', error);
            const errorMessage = error.code === 'permission-denied'
                ? 'Permission refusée. Vérifiez que vous êtes admin.'
                : `Erreur: ${error.message || 'Erreur lors de la sauvegarde'}`;
            alert(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="admin-settings-page animate-fadeIn">
            <header className="page-header">
                <div>
                    <h1 className="page-title">Paramètres</h1>
                    <p className="page-subtitle">Configurez les clés API et les modèles IA</p>
                </div>
                <Button leftIcon={<Save size={18} />} onClick={handleSave} isLoading={isSaving}>
                    Enregistrer
                </Button>
            </header>

            {saveSuccess && (
                <div className="settings-success glass">
                    <CheckCircle size={20} />
                    <p>Paramètres enregistrés avec succès !</p>
                </div>
            )}

            <div className="settings-warning glass">
                <AlertTriangle size={20} />
                <p>
                    Testez vos clés API avant de les enregistrer. Les clés existantes ne sont pas affichées.
                </p>
            </div>

            <section className="settings-section glass">
                <h2 className="section-title">
                    <Key size={20} />
                    Clés API - Intelligence Artificielle
                </h2>

                <div className="api-keys-form">
                    {/* Gemini */}
                    <div className="api-key-row">
                        <div className="input-with-status">
                            <Input
                                label="Google AI Studio (Gemini)"
                                type="password"
                                placeholder={hasExistingKeys.gemini ? '••••••••••••••••' : 'Entrez votre clé API Gemini...'}
                                value={geminiKey}
                                onChange={(e) => {
                                    setGeminiKey(e.target.value);
                                    setGeminiTestStatus('idle');
                                    setDirtyFields(prev => ({ ...prev, geminiApiKey: true }));
                                }}
                            />
                            {hasExistingKeys.gemini && geminiTestStatus === 'idle' && (
                                <span className="key-status configured">
                                    <CheckCircle size={14} /> Configurée
                                </span>
                            )}
                        </div>
                        <button
                            className={`test-btn ${geminiTestStatus}`}
                            onClick={handleTestGemini}
                            disabled={geminiTestStatus === 'testing' || !geminiKey.trim()}
                        >
                            {geminiTestStatus === 'testing' ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <FlaskConical size={16} />
                            )}
                            Tester
                        </button>
                    </div>
                    {geminiTestMessage && (
                        <div className={`test-result ${geminiTestStatus}`}>
                            {geminiTestStatus === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                            {geminiTestMessage}
                        </div>
                    )}

                    {/* Gemini available models info */}
                    {(hasExistingKeys.gemini || geminiTestStatus === 'success') && (
                        <div className="available-models-info">
                            <div className="models-info-header">
                                <Cpu size={14} />
                                <span>Modèles disponibles avec cette clé :</span>
                                <button
                                    className="refresh-models-btn"
                                    onClick={fetchGeminiModels}
                                    disabled={isLoadingGeminiModels}
                                    title="Actualiser la liste des modèles"
                                >
                                    <RefreshCw size={14} className={isLoadingGeminiModels ? 'animate-spin' : ''} />
                                </button>
                            </div>
                            {isLoadingGeminiModels ? (
                                <div className="models-loading">
                                    <Loader2 size={16} className="animate-spin" />
                                    <span>Chargement des modèles...</span>
                                </div>
                            ) : geminiModelsError ? (
                                <div className="models-error">
                                    <AlertTriangle size={14} />
                                    <span>{geminiModelsError}</span>
                                </div>
                            ) : geminiApiModels.length > 0 ? (
                                <ul className="models-info-list selectable">
                                    {geminiApiModels.map((model) => (
                                        <li
                                            key={model.id}
                                            className={`model-selectable ${selectedGeminiModels.has(model.id) ? 'selected' : ''}`}
                                            onClick={() => toggleGeminiModelSelection(model.id)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedGeminiModels.has(model.id)}
                                                onChange={() => toggleGeminiModelSelection(model.id)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <strong>{model.id}</strong>
                                            <span className={`model-type-badge ${model.type}`}>
                                                {model.type === 'image' ? 'Image' : 'Texte'}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="models-hint">Cliquez sur le bouton pour charger les modèles disponibles</p>
                            )}
                            {selectedGeminiModels.size > 0 && (
                                <div className="selected-count">
                                    {selectedGeminiModels.size} modèle(s) sélectionné(s)
                                </div>
                            )}
                        </div>
                    )}

                    {/* OpenAI */}
                    <div className="api-key-row">
                        <div className="input-with-status">
                            <Input
                                label="OpenAI (DALL-E)"
                                type="password"
                                placeholder={hasExistingKeys.openai ? '••••••••••••••••' : 'Entrez votre clé API OpenAI...'}
                                value={openaiKey}
                                onChange={(e) => {
                                    setOpenaiKey(e.target.value);
                                    setOpenaiTestStatus('idle');
                                    setDirtyFields(prev => ({ ...prev, openaiApiKey: true }));
                                }}
                            />
                            {hasExistingKeys.openai && openaiTestStatus === 'idle' && (
                                <span className="key-status configured">
                                    <CheckCircle size={14} /> Configurée
                                </span>
                            )}
                        </div>
                        <button
                            className={`test-btn ${openaiTestStatus}`}
                            onClick={handleTestOpenai}
                            disabled={openaiTestStatus === 'testing' || !openaiKey.trim()}
                        >
                            {openaiTestStatus === 'testing' ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <FlaskConical size={16} />
                            )}
                            Tester
                        </button>
                    </div>
                    {openaiTestMessage && (
                        <div className={`test-result ${openaiTestStatus}`}>
                            {openaiTestStatus === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                            {openaiTestMessage}
                        </div>
                    )}

                    {/* OpenAI available models info */}
                    {(hasExistingKeys.openai || openaiTestStatus === 'success') && (
                        <div className="available-models-info">
                            <div className="models-info-header">
                                <Cpu size={14} />
                                <span>Modèles disponibles avec cette clé :</span>
                                <button
                                    className="refresh-models-btn"
                                    onClick={fetchOpenaiModels}
                                    disabled={isLoadingOpenaiModels}
                                    title="Actualiser la liste des modèles"
                                >
                                    <RefreshCw size={14} className={isLoadingOpenaiModels ? 'animate-spin' : ''} />
                                </button>
                            </div>
                            {isLoadingOpenaiModels ? (
                                <div className="models-loading">
                                    <Loader2 size={16} className="animate-spin" />
                                    <span>Chargement des modèles...</span>
                                </div>
                            ) : openaiModelsError ? (
                                <div className="models-error">
                                    <AlertTriangle size={14} />
                                    <span>{openaiModelsError}</span>
                                </div>
                            ) : openaiApiModels.length > 0 ? (
                                <ul className="models-info-list selectable">
                                    {openaiApiModels.map((model) => (
                                        <li
                                            key={model.id}
                                            className={`model-selectable ${selectedOpenaiModels.has(model.id) ? 'selected' : ''}`}
                                            onClick={() => toggleOpenaiModelSelection(model.id)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedOpenaiModels.has(model.id)}
                                                onChange={() => toggleOpenaiModelSelection(model.id)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <strong>{model.id}</strong>
                                            <span className={`model-type-badge ${model.type}`}>
                                                {model.type === 'image' ? 'Image' : 'Texte'}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="models-hint">Cliquez sur le bouton pour charger les modèles disponibles</p>
                            )}
                            {selectedOpenaiModels.size > 0 && (
                                <div className="selected-count">
                                    {selectedOpenaiModels.size} modèle(s) sélectionné(s)
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <section className="settings-section glass">
                <h2 className="section-title">
                    <Store size={20} />
                    Boutiques Shopify
                </h2>
                <p className="section-description">
                    Connectez vos boutiques Shopify. Chaque boutique nécessite une Custom App
                    créée dans <strong>Admin Shopify → Paramètres → Apps → Développer des apps</strong>.
                </p>

                {shopConnectionSuccess && (
                    <div className="settings-success glass" style={{ marginBottom: 'var(--space-md)' }}>
                        <CheckCircle size={20} />
                        <p>{shopConnectionSuccess}</p>
                    </div>
                )}

                {/* Connected shops list */}
                {shops.length > 0 ? (
                    <div className="webhook-shops-list" style={{ marginBottom: 'var(--space-md)' }}>
                        {shops.map((shop) => (
                            <div key={shop.id} className="webhook-shop-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div className="webhook-shop-info">
                                    <div className="webhook-shop-name">
                                        <Store size={16} />
                                        <strong>{shop.name}</strong>
                                    </div>
                                    <span className="webhook-shop-domain">{shop.domain}</span>
                                </div>
                                <button
                                    onClick={() => handleDeleteShop(shop.id, shop.name)}
                                    title="Déconnecter cette boutique"
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                        color: 'var(--color-error)',
                                        borderRadius: '8px',
                                        padding: '6px 8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="webhook-empty-state" style={{ marginBottom: 'var(--space-md)' }}>
                        <Store size={32} />
                        <p>Aucune boutique connectée</p>
                        <span>Ajoutez une boutique pour commencer</span>
                    </div>
                )}

                {/* Add shop form */}
                {showAddShop ? (
                    <div className="api-keys-form" style={{ position: 'relative' }}>
                        <button
                            className="test-btn"
                            onClick={() => { setShowAddShop(false); setShopConnectionError(''); }}
                            style={{ position: 'absolute', top: 0, right: 0, minWidth: 'auto', padding: '4px 8px' }}
                        >
                            <X size={16} />
                        </button>
                        <div style={{ background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.2)', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', fontSize: '0.85rem', lineHeight: '1.5' }}>
                            <strong style={{ display: 'block', marginBottom: '6px' }}>📋 Mémo — Configuration de la Custom App :</strong>
                            <ol style={{ margin: 0, paddingLeft: '18px' }}>
                                <li>Admin Shopify → Paramètres → Apps → <strong>Développer des apps</strong></li>
                                <li>Créer une app (nom libre)</li>
                                <li>Configurer les scopes API Admin : <code style={{ background: 'rgba(255,255,255,0.1)', padding: '1px 4px', borderRadius: '3px' }}>write_products</code></li>
                                <li>Installer l'app</li>
                                <li>Copier le <strong>Client ID</strong> et le <strong>Client Secret</strong> (onglet Paramètres)</li>
                            </ol>
                            <div style={{ marginTop: '10px', padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', fontSize: '0.8rem' }}>
                                <strong style={{ display: 'block', marginBottom: '6px' }}>🔗 URLs à renseigner dans la Custom App :</strong>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ color: 'rgba(255,255,255,0.5)', minWidth: '100px' }}>App URL :</span>
                                        <code
                                            onClick={() => { navigator.clipboard.writeText('https://ai-shopify-image-gen.web.app'); }}
                                            style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: '3px', cursor: 'pointer', userSelect: 'all' }}
                                            title="Cliquer pour copier"
                                        >https://ai-shopify-image-gen.web.app</code>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ color: 'rgba(255,255,255,0.5)', minWidth: '100px' }}>Redirect URL :</span>
                                        <code
                                            onClick={() => { navigator.clipboard.writeText('https://ai-shopify-image-gen.web.app/auth/callback'); }}
                                            style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: '3px', cursor: 'pointer', userSelect: 'all' }}
                                            title="Cliquer pour copier"
                                        >https://ai-shopify-image-gen.web.app/auth/callback</code>
                                    </div>
                                </div>
                                <span style={{ display: 'block', marginTop: '4px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' }}>💡 Cliquer sur une URL pour la copier</span>
                            </div>
                        </div>
                        <Input
                            label="Domaine Shopify"
                            placeholder="ma-boutique.myshopify.com"
                            value={newShopDomain}
                            onChange={(e) => setNewShopDomain(e.target.value)}
                        />
                        <div style={{ display: 'flex', gap: '8px', margin: '8px 0' }}>
                            <button
                                type="button"
                                onClick={() => setShopAuthMode('credentials')}
                                style={{
                                    flex: 1, padding: '8px', borderRadius: '6px', cursor: 'pointer',
                                    border: shopAuthMode === 'credentials' ? '1px solid var(--color-primary)' : '1px solid rgba(255,255,255,0.1)',
                                    background: shopAuthMode === 'credentials' ? 'rgba(99,102,241,0.2)' : 'transparent',
                                    color: 'var(--color-text)', fontSize: '0.85rem',
                                }}
                            >
                                Client ID + Secret
                            </button>
                            <button
                                type="button"
                                onClick={() => setShopAuthMode('token')}
                                style={{
                                    flex: 1, padding: '8px', borderRadius: '6px', cursor: 'pointer',
                                    border: shopAuthMode === 'token' ? '1px solid var(--color-primary)' : '1px solid rgba(255,255,255,0.1)',
                                    background: shopAuthMode === 'token' ? 'rgba(99,102,241,0.2)' : 'transparent',
                                    color: 'var(--color-text)', fontSize: '0.85rem',
                                }}
                            >
                                Access Token
                            </button>
                        </div>
                        {shopAuthMode === 'credentials' ? (
                            <>
                                <Input
                                    label="Client ID"
                                    type="password"
                                    placeholder="ID client de l'app..."
                                    value={newShopClientId}
                                    onChange={(e) => setNewShopClientId(e.target.value)}
                                />
                                <Input
                                    label="Client Secret"
                                    type="password"
                                    placeholder="Secret de l'app..."
                                    value={newShopClientSecret}
                                    onChange={(e) => setNewShopClientSecret(e.target.value)}
                                />
                            </>
                        ) : (
                            <Input
                                label="Admin API Access Token"
                                type="password"
                                placeholder="shpat_xxxxxxxxxxxxxxxxxxxxx"
                                value={newShopAccessToken}
                                onChange={(e) => setNewShopAccessToken(e.target.value)}
                            />
                        )}
                        {shopConnectionError && (
                            <div className="test-result error">
                                <AlertTriangle size={14} />
                                {shopConnectionError}
                            </div>
                        )}
                        <Button
                            leftIcon={isConnectingShop ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                            onClick={handleConnectShop}
                            isLoading={isConnectingShop}
                        >
                            Connecter la boutique
                        </Button>
                    </div>
                ) : (
                    <Button
                        leftIcon={<Plus size={16} />}
                        onClick={() => { setShowAddShop(true); setShopConnectionError(''); setShopConnectionSuccess(''); }}
                        variant="secondary"
                    >
                        Ajouter une boutique
                    </Button>
                )}
            </section>

            {/* Webhook Registration Section */}
            <section className="settings-section glass">
                <h2 className="section-title">
                    <Radio size={20} />
                    Synchronisation Temps Réel (Webhooks)
                </h2>
                <p className="section-description">
                    Les webhooks permettent de synchroniser automatiquement les modifications de produits depuis Shopify.
                    Sans webhooks, la synchronisation ne se fait que manuellement via le bouton "🔄 Synchroniser".
                </p>

                {shops.length === 0 ? (
                    <div className="webhook-empty-state">
                        <Store size={32} />
                        <p>Aucune boutique connectée</p>
                        <span>Connectez une boutique Shopify pour activer les webhooks</span>
                    </div>
                ) : (
                    <div className="webhook-shops-list">
                        {shops.map((shop) => (
                            <div key={shop.id} className="webhook-shop-item">
                                <div className="webhook-shop-info">
                                    <div className="webhook-shop-name">
                                        <Store size={16} />
                                        <strong>{shop.name}</strong>
                                    </div>
                                    <span className="webhook-shop-domain">{shop.domain}</span>

                                    {/* Status */}
                                    <div className={`webhook-status ${shop.webhooksRegistered && shop.webhooksRegistered.length > 0 ? 'active' : 'inactive'}`}>
                                        {shop.webhooksRegistered && shop.webhooksRegistered.length > 0 ? (
                                            <>
                                                <Zap size={14} />
                                                <span>Actif ({shop.webhooksRegistered.length} webhooks)</span>
                                            </>
                                        ) : (
                                            <>
                                                <AlertTriangle size={14} />
                                                <span>Non configuré</span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <button
                                    className="webhook-register-btn"
                                    onClick={() => handleRegisterWebhooks(shop.id)}
                                    disabled={webhookLoadingShop === shop.id}
                                >
                                    {webhookLoadingShop === shop.id ? (
                                        <Loader2 size={16} className="animate-spin" />
                                    ) : (
                                        <Radio size={16} />
                                    )}
                                    {shop.webhooksRegistered && shop.webhooksRegistered.length > 0
                                        ? 'Réenregistrer'
                                        : 'Activer les webhooks'}
                                </button>

                                {webhookMessage && webhookMessage.shopId === shop.id && (
                                    <div className={`webhook-message ${webhookMessage.type}`}>
                                        {webhookMessage.type === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                                        {webhookMessage.text}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="webhook-info-box">
                    <strong>📋 Webhooks enregistrés :</strong>
                    <ul>
                        <li><code>products/create</code> - Quand un produit est créé</li>
                        <li><code>products/update</code> - Quand un produit est modifié</li>
                        <li><code>products/delete</code> - Quand un produit est supprimé</li>
                    </ul>
                </div>
            </section>
        </div>
    );
}
